import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { faBox, faBoxes, faChevronRight, faFileInvoice, faLongArrowAltRight, faPlus, faQrcode, faTag } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { AggregateColumnModel, AggregateRowModel, ColumnModel, FilterSettingsModel, GridComponent, GroupSettingsModel, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import * as moment from 'moment';
import * as _ from 'underscore';
import { AuthService } from '../../../../../@core/auth/auth.service';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { KragUtilitiesService } from '../../../../../@core/utils/krag-utilities.service';
import { KwFilterDropdownService } from '../../../../core/widgets/kw-filter-dropdown/kw-filter-dropdown.service';
import { FilterSettings } from '../../../../core/widgets/kw-filter-dropdown/models/filter-options';
import { FilterResults } from '../../../../core/widgets/kw-filter-dropdown/models/filter-results';
import { InventoryService } from '../../../pick-plan/services/inventory.service';
import { HandlingUnitBulkMove } from '../../models/handling-unit-bulk-actions';
import { HandlingUnitMasterDTO } from '../../models/handling-unit-master';
import { inventoryStatusClass } from '../../models/inventory-status';
import { InventorySessionService } from '../../services/inventory-session.service';
import { InventorySummaryPdfService } from '../../services/inventory-summary-pdf.service';
import { BulkMoveSelectionPopupComponent } from './bulk-move-selection-popup/bulk-move-selection-popup.component';
import { InventoryHumEditComponent } from './inventory-hum-edit/inventory-hum-edit.component';
import { InventoryScanPopupComponent } from './inventory-scan-popup/inventory-scan-popup.component';
import { packageBreakdown } from '../../models/package-breakdown';
import { KW_QUANTITY_FORMAT } from '../../../../../@core/utils/ejs-constants';


export interface InventorySummaryTableBaseItem {
  id?: number;
  // Hum Values
  updatedDate?: Date;
  createdDate?: Date|string;
  createdBy?: string;
  createdByUserId?: number;
  barcode?: string;
  roomLocationId?: number | null;
  roomLocationName?: string;
  roomId?: number | null;
  roomName?: string;
  warehouseId?: number | null;
  warehouseName?: string;
  inventoryStatusId?: number | null;
  inventoryStatusName?: string;
  integrationId?: string | null;
  boxStart?: string | null;
  boxEnd?: string | null;
  dateIn?: Date | null;
  dateOut?: Date | null;
  isActive?: boolean;
  // hud values
  quantity?: number;
  itemId?: number | null;
  itemName?: string;
  itemDescription?: string;
  blockId?: number | null;
  blockName?: string;
  cropId?: number;
  cropName?: string;
  varietyName?: string;
  varietyId?: number | null;
  varietyColor?: string;
  varietyShortName?: string;
  farmName?: string;
  farmId?: number | null;
  fieldman?: string;
  famousIntegrationId?: string;
  lotId?: number;
  lotNumber?: string;
  loadId?: number | null;
  loadNumber?: string;
  cropYearId?: number | null;
  cropYear?: string;
  lotExperationDate?: string;
  alphaLotAttribute?:string;

  parentItemId?: number;
  parentItem?: string;

  packageComponentName?: string;
  packageComponentQuantityPerPackage?: number;
  packageComponentUnitOfMeasure?: string;
  packageComponentWeight?: number;
  packageComponentWeightUnitOfMeasure?: string;
  packageDescription?: string;
  packageHasComponents?: boolean;
  packageName?: string;
  packageQuantityPerPallet?: number;
  packageUnitOfMeasure?: string;

  lastTransaction?: string;

  orderNumber?: string;
  orderId?: number;
  weight?: number;
}
export interface InventorySummaryMobileCard {
  id: number;
  isActive: boolean;
  barcode: string;
  statusName: string;
  productName: string;
  extraItemCount: number;
  lotNumber: string;
  alphaLotAttribute: string;
  orderNumber: string;
  quantity: number;
  weight: number;
  createdDate: Date | null;
}

const MOBILE_PAGE_SIZE = 25;

@Component({
  selector: 'inventory-summary',
  templateUrl: './inventory-summary.component.html',
  styleUrls: ['./inventory-summary.component.scss']
})
export class InventorySummaryComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  faBox = faBox;
  faBoxes = faBoxes;
  faChevronRight = faChevronRight;
  faFileInvoice = faFileInvoice;
  faLongArrowAltRight = faLongArrowAltRight;
  faPlus = faPlus;
  faQrcode = faQrcode;
  faTag = faTag;

  public pageSize = 50;
  public currentPage = 1;

  public mobileCards: InventorySummaryMobileCard[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;

  public groupOptions: GroupSettingsModel = {
    columns: ['barcode'],
    showDropArea: true

  };


  @ViewChild('actionTemplate', { static: true })
  public actionTemplate: any;

  @ViewChild('grid', { static: false }) grid: GridComponent;
  public toolbarOptions: string[] = ['ExcelExport','PdfExport'];

  public pageSettings: PageSettingsModel = {
    pageSize: 50,
    pageSizes: [50, 100, 200, 250]
  }

  public filterSettings: FilterSettingsModel = {
    mode: "Immediate",
    type: "Excel"

  }


  public aggregates: AggregateRowModel[] = [];

  public aggregate: AggregateRowModel = {
    columns: [{
      type: 'Sum',
      field: 'inventoryStatusName',
      format: 'C2',
      footerTemplate: 'Totals:'
    }]
  };

  public items: HandlingUnitMasterDTO[] = [];
  public filteredItems: HandlingUnitMasterDTO[] = [];

  public tableData: InventorySummaryTableBaseItem[] = [];
  public displayedColumns: ColumnModel[] = [];
  public baseColumns: ColumnModel[] = [];
  @ViewChild('statusTemplate', { static: true })
  public statusTemplate: any;

  public isLoading: boolean = true;
  private initialLoad: boolean = true;
  private currentIncludeInactive: boolean | string = false;

  public dropdownSettings: FilterSettings = {
    inventoryItemSettings: {
      display: true,
      singleSelection: false
    },
    locationSettings: {
      display: true,
      singleSelection: false
    },
    varietySettings: {
      display: false,
      singleSelection: false,
    },
    roomSettings: {
      display: true,
      singleSelection: false,
    },
    warehouseSettings: {
      display: true,
      singleSelection: false
    },
    lotNumberSettings: {
      display: true,
      singleSelection: false
    },
    includeCompletedSettings: {
      display: true,
      singleSelection: true,
      label: "Include Inactive?",
    }
  };
  get gridMessageAndCount() {
    let pageSize = (this.grid && this.grid.pageSettings) ? this.grid.pageSettings.pageSize: 0;
    let totalCount = (this.grid && this.grid.pageSettings) ? this.grid.pageSettings.totalRecordsCount: 0;
    let currentPageCount = pageSize && totalCount ? Math.min(pageSize, totalCount) : this.tableData.length;
    return {
      message: "Narrow results by products, locations, and lots.",
      count: currentPageCount,
      showPdf:true,
      showExcel:true,
      pageSize: pageSize,
      totalCount: totalCount
    };
  }

  constructor(
    private inventoryService: InventoryService,
    private errorService: ErrorService,
    private changeDetRef: ChangeDetectorRef,
    private session: InventorySessionService,
    private toastr: NbToastrService,
    public filterService: KwFilterDropdownService,
    private router: Router,
    public dialog: MatDialog,
    private kragUtils: KragUtilitiesService,
    public authService: AuthService,
    private inventorySummaryPdfService: InventorySummaryPdfService,
  ) { }

  ngOnInit() {

    this.baseColumns = [
      { headerText: 'Barcode', field: 'barcode' },
      { headerText: 'Status', field: 'inventoryStatusName', template: this.statusTemplate, width: 140 },
      { headerText: 'Product', field: 'itemName' },
      { headerText: 'Lot', field: 'lotNumber' },
      { headerText: 'Alpha', field: 'alphaLotAttribute', width: 100 },
      { headerText: 'Order #', field: 'orderNumber' },
      { headerText: 'Weight', field: 'weight', format: "N0", width: 120 },
    ]

    this.loadData();

    this.pageSettings = {
      pageSize: this.pageSize,
      currentPage: this.currentPage
    }


  }

  loadData(includeInactive: boolean = false) {
    this.inventoryService.getInventorySummary(includeInactive).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
      this.items = x;
      this.filteredItems = this.items;
      
      // Update current state to the filter value that triggered this load
      const currentFilterState = this.filterService.getFilterState();
      this.currentIncludeInactive = currentFilterState.includeCompleted;

      this.filterService.filter();

      this.isLoading = false;
    }, error:err => {
      this.errorService.handleError(err);
      this.isLoading = false;
    }});
  }
  toolbarClick(args: any) {
    if (args.item.id === 'grid_pdfexport') {
    this.grid.pdfExport({
    fileName: 'InventorySummary.pdf',
  })
    }

    if (args.item.id === 'grid_excelexport') {
      this.grid.excelExport();
    }
  }

  openDialog(row: any) {
    
    this.router.navigateByUrl("/harvest-engine/inventory/inventory-summary-popup?humId=" + row.id + "&isActive=" + row.isActive);
  }


  createHUM() {

    InventoryHumEditComponent.open(this.dialog, { Hums: this.items })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
        if (event)
          this.openDialog(event);
      });

  }

  moveBulkHUM() {
    if (this.filterService.filterResults.selectedInventoryItems.length == 0 && this.filterService.filterResults.selectedWarehouses.length == 0 &&
      this.filterService.filterResults.selectedRooms.length == 0 && this.filterService.filterResults.selectedLocations.length == 0 &&
      this.filterService.filterResults.selectedLotNumbers.length == 0) {
      this.toastr.warning("Please select at least one filter to move inventory.", "Warning!");
    }
    else if (this.tableData.length == 0) {
      this.toastr.warning("Filtered inventory must be available to move.", "Warning!");
    }
    else {
      let bulkMoveList: HandlingUnitBulkMove[] = [];

      let groupData = this.tableData.reduce((r, a) => {
        r[a.barcode] = [...r[a.barcode] || [], a];
        return r;
      }, {});


      const barcodes = Object.keys(groupData);
      for (const barcode of barcodes) {
        const x = groupData[barcode];

        let warehouse = this.filterService.allWarehouses.find(y => y.id == x[0].warehouseId);
        let room = this.filterService.allRooms.find(y => y.id == x[0].roomId);
        let location = this.filterService.allLocations.find(y => y.id == x[0].roomLocationId);

        const uniqueItemsSet = new Set();
        x.forEach(item => {
          const value = item["itemName"];
          uniqueItemsSet.add(value);
        });

        const uniqueLotSet = new Set();
        x.forEach(item => {
          const value = item["lotNumber"];
          uniqueLotSet.add(value);
        });

        bulkMoveList.push({
          humId: x[0].id,
          warehouseId: x[0].warehouseId ? x[0].warehouseId : 0,
          roomId: x[0].roomId ? x[0].roomId : 0,
          roomLocationId: x[0].roomLocationId ? x[0].roomLocationId : 0,
          roomLocationName: location ? location.name : "",
          roomName: room ? room.name : "",
          warehouseName: warehouse ? warehouse.name : "",
          tagNumber: x[0].barcode,
          details:  Array.from(uniqueItemsSet).join(', '), //(x.handlingUnitDetails.length > 0 ? x.handlingUnitDetails[0].itemName : ""),
          lot: Array.from(uniqueLotSet).join(', '), //(x.handlingUnitDetails.length > 0 ? x.handlingUnitDetails[0].lotNumber : ""),
          isMultiple: true,
          boxStart :  x[0].boxStart ? x[0].boxStart : '',
          boxEnd :  x[0].boxEnd ? x[0].boxEnd : '',
          integrationId : x[0].integrationId ? x[0].integrationId: '',
          dateIn : x[0].dateIn ?  x[0].dateIn : '',
          dateOut : x[0].dateOut ? x[0].dateOut : ''
        });

        if (!bulkMoveList[0].handlingUnitMasterIds) {
          bulkMoveList[0].handlingUnitMasterIds = [];
          bulkMoveList[0].handlingUnitMasterIds.push(x[0].id);
        } else {
          bulkMoveList[0].handlingUnitMasterIds.push(x[0].id);
        }
      }
      this.session.handlingUnitBulkMoveList = bulkMoveList;
      this.showBulkMoveSelectionDialog();
    }
  }

  showBulkMoveSelectionDialog() {
    const dialogRef = this.dialog.open(BulkMoveSelectionPopupComponent, {
      minWidth: '550px',
      data: {}
    });
  }

  createBulkHUM() {

    InventoryHumEditComponent.open(this.dialog, { Hums: this.items, isBulkCreate: true })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
        if (event)
          this.loadData();
      });

  }

  getFooterTotal() {
    return this.filteredItems.reduce((accumulator, obj) => {
      return accumulator + this.getRowQTY(obj);
    }, 0);
  }

  // getFooterAggTotal() {
  //   var val = 0;
  //   this.tableData.forEach(x => {
  //     x.cu.forEach(c => {
  //       c.storageTypes.forEach(z => val += z.total)
  //     })
  //   })
  //   return formatNumber(val, 'en-US', '1.0-0');
  // }

  scan() {
    const dialogRef = this.dialog.open(InventoryScanPopupComponent, {
      minWidth: '30%',
      data: {
        Hums: this.items
      }
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
      if (x.isHum)
        this.openDialog(x.hum);


      this.changeDetRef.detectChanges();
    });
  }


  getLocation(row: HandlingUnitMasterDTO) {
    var array = [row.warehouseName, row.roomName, row.roomLocationName].filter(x => x);

    return array.join(", ");

  }

  getRowQTY(row: HandlingUnitMasterDTO) {
    return row.handlingUnitDetails.reduce((acc, cur) => acc + cur.quantity, 0);
  }

  setFilterResults(results: FilterResults) {
    if (this.initialLoad) {
      this.initialLoad = false;
      results = this.filterService.getFilterState();
    }
    
    // Check if includeInactive setting has changed
    if (this.currentIncludeInactive !== results.includeCompleted) {
      this.isLoading = true;
      // For API call, treat 'isLast' as true to get inactive inventory
      const includeInactiveForAPI = (results.includeCompleted === true) || ((results.includeCompleted as any) === 'isLast');
      this.loadData(includeInactiveForAPI);
      return;
    }
    
    let filteredResults = this.items.filter(x => {
      // Handle active inventory
      if (x.isActive) {
        return true;
      }
      
      // Handle inactive inventory based on includeCompleted value
      if (results.includeCompleted === true) {
        return true; // Include all inactive
      } else if ((results.includeCompleted as any) === 'isLast') {
        // Include inactive items updated within last 7 days
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const itemUpdatedDate = new Date(x.updatedDate);
        return itemUpdatedDate >= sevenDaysAgo;
      }
      
      return false; // Exclude inactive when includeCompleted is false
    }).filter(x =>
      (results.selectedWarehouses.map(y => y.id).includes(x.warehouseId) || results.selectedWarehouses.length == 0) &&
      (results.selectedInventoryItems.length == 0 || this.kragUtils.filterArrayByArray(x.handlingUnitDetails.map(y => y.itemId), results.selectedInventoryItems.map(y => y.id))) &&
      (results.selectedRooms.map(y => y.id).includes(x.roomId) || results.selectedRooms.length == 0) &&
      (results.selectedLocations.map(y => y.id).includes(x.roomLocationId) || results.selectedLocations.length == 0) &&
      (results.selectedVarieties.length == 0 || this.kragUtils.filterArrayByArray(x.handlingUnitDetails.map(y => y.varietyId), results.selectedVarieties.map(y => y.id))) &&
      (results.selectedLotNumbers.length == 0 || this.kragUtils.filterArrayByArray(x.handlingUnitDetails.map(y => y.lotId), results.selectedLotNumbers.map(y => y.id)))
      //(results.selectedVarieties.map(y => y.id).includes(x.handlingUnitDetails.map(y =>).) || results.selectedVarieties.length == 0)
    );

    this.filteredItems = [...filteredResults];

    this.setTableData();

  }

  setTableData() {
    this.displayedColumns = [];
    this.aggregates = []
    this.tableData = [];

    this.mobileCards = this.filteredItems.map(x => this.toMobileCard(x));
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;

    let uniqItemAttr = _.uniq(this.filteredItems.flatMap(x => x.handlingUnitDetails).flatMap(x => x.itemAttributeValues).map(x => <any>{ name: x.name + "Cust", id: x.inventoryItemAttributeId }), true, x => x.name);

    let uniqComponents = _.uniq(this.filteredItems.flatMap(x => x.handlingUnitDetails).map(x => x.packageComponentName));
    let uniqUnitOfMeasures = _.uniq(this.filteredItems.flatMap(x => x.handlingUnitDetails).map(x => x.packageUnitOfMeasure));

    for (let hum of this.filteredItems) {
      for (let hud of hum.handlingUnitDetails) {
        let tableRecord: InventorySummaryTableBaseItem = {
          barcode: hum.barcode,
          blockId: hud.blockId,
          id: hum.id,
          isActive: hum.isActive,
          blockName: hud.blockName,
          createdBy: hum.createdBy,
          createdByUserId: hum.createdByUserId,
          createdDate: hum.createdDate ? new Date(hum.createdDate) : null,
          cropId: hud.cropId,
          cropName: hud.cropName,
          cropYear: hud.cropYear,
          cropYearId: hud.cropYearId,
          famousIntegrationId: hud.famousIntegrationId,
          farmId: hud.farmId,
          farmName: hud.farmName,
          fieldman: hud.fieldman,
          inventoryStatusId: hum.inventoryStatusId,
          //inventoryStatusName: (hum.inventoryStatusName) ? hum.inventoryStatusName : 'Available',
          inventoryStatusName: hum.inventoryStatusName,
          itemDescription: hud.itemDescription,
          itemId: hud.itemId,
          itemName: hud.itemName,
          loadId: hud.loadId,
          loadNumber: hud.loadNumber,
          lotExperationDate: hud.lotExperationDate,
          lotId: hud.lotId,
          lotNumber: hud.lotNumber,
          orderId: hum.orderId,
          orderNumber: hum.orderNumber,
          packageComponentName: hud.packageComponentName,
          packageComponentQuantityPerPackage: hud.packageComponentQuantityPerPackage,
          packageComponentUnitOfMeasure: hud.packageComponentUnitOfMeasure,
          packageComponentWeight: hud.packageComponentWeight,
          packageComponentWeightUnitOfMeasure: hud.packageComponentWeightUnitOfMeasure,
          packageDescription: hud.packageDescription,
          packageHasComponents: hud.packageHasComponents,
          packageName: hud.packageName,
          packageQuantityPerPallet: hud.packageQuantityPerPallet,
          packageUnitOfMeasure: hud.packageUnitOfMeasure,
          parentItem: hud.parentItem,
          parentItemId: hud.parentItemId,
          quantity: hud.quantity,
          roomId: hum.roomId,
          roomLocationId: hum.roomLocationId,
          roomLocationName: hum.roomLocationName,
          roomName: hum.roomName,
          updatedDate: hum.updatedDate,
          varietyColor: hud.varietyColor,
          varietyId: hud.varietyId,
          varietyName: hud.varietyName,
          varietyShortName: hud.varietyShortName,
          warehouseId: hum.warehouseId,
          warehouseName: hum.warehouseName,
          lastTransaction: "",
          weight: hud.weight,
          boxStart:hum.boxStart,
          boxEnd:hum.boxEnd,
          integrationId : hum.integrationId,
          dateIn : hum.dateIn,
          dateOut : hum.dateOut,
          alphaLotAttribute: !!hud.lotAttributeValues && !!hud.lotAttributeValues.find(x=>x.customAttributeName == "AA%" && x.isActive) ? hud.lotAttributeValues.find(x=>x.customAttributeName == "AA%" && x.isActive).value : ""
        };

        if (hum.transactions && hum.transactions.length > 0)
          tableRecord.lastTransaction = moment(_.sortBy(hum.transactions, x => x.createdDate).reverse()[0].createdDate).format('LL');

        for (let attr of uniqItemAttr) {
          let result = hud.itemAttributeValues.find(x => x.inventoryItemAttributeId == attr.id);
          tableRecord[attr.name] = result ? result.value : "";
        }

        // uom / quantity set
        let setQuantity = true;

        if (hud.packageUnitOfMeasure) {
          if (hud.packageHasComponents) {
            const { packages, components } = packageBreakdown(hud.quantity, hud.packageComponentQuantityPerPackage);
            tableRecord[hud.packageUnitOfMeasure] = packages;
            tableRecord[hud.packageComponentName] = components;
          }

          setQuantity = false;
        }

        // if (!setQuantity)
        //   tableRecord.quantity = "";

        this.tableData.push(tableRecord);
      }
    }

    this.aggregate = {
      columns: [{
        type: 'Sum',
        field: 'inventoryStatusName',
        format: 'C2',
        footerTemplate: 'Totals:'
      }]
    };

    this.displayedColumns.push(...this.baseColumns);
    // this.displayedColumns.push(...uniqItemAttr.map(x => <ColumnModel>{ field: x.name, headerText: x.name.slice(0, -4), autoFit: true}));
    this.displayedColumns.push({ headerText: 'Quantity', field: 'quantity', format: KW_QUANTITY_FORMAT, width: 120 });
    // this.displayedColumns.push(...uniqUnitOfMeasures.map(x => <ColumnModel>{ field: x, headerText: x, autoFit: true, format: 'N2'}));
    // this.displayedColumns.push(...uniqComponents.map(x => <ColumnModel>{ field: x, headerText: x, autoFit: true, format: 'N2'}));

    this.aggregate.columns.push(...uniqUnitOfMeasures.map(x => <AggregateColumnModel>{
      field: x,
      type: 'Sum',
      format: 'N2',
      footerTemplate: '${Sum}'
    }));

    this.aggregate.columns.push(...uniqComponents.map(x => <AggregateColumnModel>{
      field: x,
      type: 'Sum',
      format: 'N2',
      footerTemplate: '${Sum}'
    }));

    this.displayedColumns.push({
      field: 'createdDate',
      headerText: "Created Date",
      type: 'date',
      format: { type: 'date', format: 'MM/dd/yyyy' },
      width: 140
    });

    // this.displayedColumns.push({
    //   headerText: "Last Transaction",
    //   field: 'lastTransaction',
    //   autoFit: true
    // });

    this.displayedColumns.push({
      headerText: "",
      template: this.actionTemplate,
      width: 80,
      allowFiltering: false,
      allowSorting: false,
      allowGrouping: false
    });


    this.aggregates.push(this.aggregate);
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByCard(_: number, card: InventorySummaryMobileCard): number {
    return card.id;
  }

  private toMobileCard(hum: HandlingUnitMasterDTO): InventorySummaryMobileCard {
    const details = hum.handlingUnitDetails ?? [];
    const first = details[0];
    const alpha = first?.lotAttributeValues?.find(x => x.customAttributeName == "AA%" && x.isActive);
    return {
      id: hum.id,
      isActive: hum.isActive,
      barcode: hum.barcode,
      statusName: hum.inventoryStatusName,
      productName: first?.itemName ?? '',
      extraItemCount: Math.max(details.length - 1, 0),
      lotNumber: first?.lotNumber ?? '',
      alphaLotAttribute: alpha?.value ?? '',
      orderNumber: hum.orderNumber ?? '',
      quantity: details.reduce((acc, cur) => acc + (cur.quantity ?? 0), 0),
      weight: details.reduce((acc, cur) => acc + (cur.weight ?? 0), 0),
      createdDate: hum.createdDate ? new Date(hum.createdDate) : null,
    };
  }

  public readonly getStatusClass = inventoryStatusClass;


  downloadFiles(event: any) {
  if (event === 'pdfExport') {
    const docDefinition = this.inventorySummaryPdfService.buildDocDefinition(this.tableData);
    this.inventorySummaryPdfService.downloadPdf(docDefinition, 'InventorySummary.pdf');
  }

  if (event === 'excelExport') {
    this.grid.excelExport({
      fileName: 'InventorySummary.xlsx'
    });
  }
  }
}
